/**
 * Pure helpers for the Auto-invoicing page (tracker item 37). Amounts come
 * from the server as decimal strings; the only client arithmetic is the
 * total of the selected groups, done in integer cents.
 */

import * as types from '../api/types';
import { getErrorCode, getErrorMessage } from '../utils/apiError';
import { centsToDecimal, toCents } from './financeChargeUi';
import { invoiceDate, invoiceMoney } from './invoiceUi';

export const AUTO_INVOICING_PATH = '/invoices/auto-invoicing';

/** Every group is selected by default (they are what a run would invoice). */
export function defaultGroupSelection(groups: types.AutoInvoicePreviewGroup[]): string[] {
  return groups.map((group) => group.key);
}

/** Select-all checkbox: every group, or nothing when all are selected. */
export function toggleAllGroups(groups: types.AutoInvoicePreviewGroup[], selected: string[]): string[] {
  const keys = groups.map((group) => group.key);
  return keys.length > 0 && keys.every((key) => selected.includes(key)) ? [] : keys;
}

/** Total (subtotal + tax) of the selected groups. */
export function selectedGroupsTotal(groups: types.AutoInvoicePreviewGroup[], selected: string[]): string {
  const cents = groups
    .filter((group) => selected.includes(group.key))
    .reduce((sum, group) => sum + toCents(group.total), 0);
  return centsToDecimal(cents);
}

export function runConfirmMessage(count: number, total: string, autoSend: boolean): string {
  const invoices = `${count} ${count === 1 ? 'invoice' : 'invoices'}`;
  const then = autoSend
    ? ' They will be sent right away (and emailed when notifications are on), as auto-send is on.'
    : ' They are created as drafts for you to review and send.';
  return `Create ${invoices} totaling ${invoiceMoney(total)} before discounts, dated today?${then}`;
}

export const MODE_LABELS: Record<string, string> = {
  OFF: 'Off',
  ON_COMPLETION: 'When a job is completed',
  NIGHTLY: 'Nightly',
};

/** What the tenant's mode means for this page. */
export function autoInvoicingModeDescription(mode: string, nightlyHour: number): string {
  const hour = `${String(nightlyHour).padStart(2, '0')}:00 UTC`;
  switch (mode) {
    case 'ON_COMPLETION':
      return `Per-visit customers' jobs are invoiced as soon as they are completed; the nightly run (from ${hour}) invoices consolidated customers after month end and anything missed. Use this page to run it now.`;
    case 'NIGHTLY':
      return `Every eligible job is invoiced once a day, from ${hour}. Use this page to run it now.`;
    default:
      return 'Auto-invoicing is off: nothing is invoiced automatically. You can still invoice the groups below by hand here. An administrator can turn it on under Settings > Company.';
  }
}

const REASON_LABELS: Record<string, string> = {
  NOT_COMPLETED: 'Not completed',
  ALREADY_INVOICED: 'Already invoiced',
  CANCELLED: 'Cancelled',
  COVERED_BY_FLAT_CONTRACT: 'Covered by a flat contract',
  CUSTOMER_EXCLUDED: 'Customer excluded',
  ON_HOLD: 'On hold',
  // No longer produced (cancel unlinks the invoice's job lines); kept for run history recorded before
  LINES_ALREADY_INVOICED: 'Was on a cancelled invoice',
  NO_LINE_ITEMS: 'No work items',
  ZERO_TOTAL: 'Zero total',
  BEFORE_START_DATE: 'Before the start date',
  NO_COMPLETED_DATE: 'No completion date',
  WAITING_FOR_MONTH_END: 'Waiting for month end',
  NOT_ELIGIBLE: 'Nothing eligible',
  ERROR: 'Error',
};

export function skipReasonLabel(reason: string | null | undefined): string {
  if (!reason) return '';
  return REASON_LABELS[reason] ?? reason;
}

export const TRIGGER_LABELS: Record<string, string> = {
  MANUAL: 'Manual',
  ON_COMPLETION: 'On completion',
  NIGHTLY: 'Nightly',
};

/** "Consolidated, 09/01/2026 – 09/30/2026" or "Per visit". */
export function groupMethodLabel(group: Pick<types.AutoInvoicePreviewGroup, 'billingMethod' | 'periodStartDate' | 'periodEndDate'>): string {
  if (group.billingMethod === 'CONSOLIDATED' && group.periodStartDate && group.periodEndDate) {
    return `Monthly, ${invoiceDate(group.periodStartDate)} – ${invoiceDate(group.periodEndDate)}`;
  }
  return 'Per visit';
}

/** A user-facing message for a preview / run failure. */
export function autoInvoicingError(err: unknown, fallback: string): string {
  switch (getErrorCode(err)) {
    case 'GROUP_NOT_FOUND':
      return 'One of the selected jobs or customers no longer exists. Nothing was invoiced. Refresh the preview and try again.';
    default:
      return getErrorMessage(err, fallback);
  }
}
